/* eslint-disable prettier/prettier */
import React, { useState, useEffect, useMemo } from 'react';

import AppBar from 'components/AppBar';
import GroupedList from 'components/GroupedList';
import GroupedItem from 'components/GroupedList/components/GroupedItem';
import Loader from 'components/Loader';
import ModalHeader from 'components/ModalHeader';
import { Desktop, Mobile } from 'components/Responsive';
import Search from 'components/Search';
import useIsMobile from 'hooks/useIsMobile';
import useStoreTranslation from 'hooks/useStoreTranslation';
import { GroupedListModel } from 'models/groupedList';
import { htmlT } from 'utils/generic';

import {
  Container,
  StyledButton,
  ButtonContainer,
  List,
  FiltersContainer,
  FilterButton,
  GroupIcon,
  UserIcon,
} from './styles';

interface Props {
  userList: GroupedListModel[];
  selectedUsers: GroupedListModel[];
  groupList: GroupedListModel[];
  selectedGroups: GroupedListModel[];
  isLoading: boolean;
  onConfirmSelection: (selected: GroupedListModel[]) => void;
  onCloseSelectionScreen: () => void;
}

type FilterState = null | 'user' | 'group';

const sortByName = (list: GroupedListModel[]) =>
  list.sort((a, b) => {
    if (a.disabled) return 1;
    if (b.disabled) return 1;

    return a?.name || '' > (b?.name || '') ? -1 : 1;
  });

const GroupsAndUsersSelection = ({
  userList,
  selectedUsers,
  groupList,
  selectedGroups,
  isLoading,
  onConfirmSelection,
  onCloseSelectionScreen,
}: Props) => {
  const { t } = useStoreTranslation();
  const isMobile = useIsMobile();
  const [usersResult, setUsersResult] = useState<GroupedListModel[]>([]);
  const [groupsResult, setGroupsResult] = useState<GroupedListModel[]>([]);
  const [selectedUser, setSelectedUser] = useState<GroupedListModel[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<GroupedListModel[]>([]);
  const [showSearch, setShowSearch] = useState(false);
  const [filter, setFilter] = useState<FilterState>(null);
  const [searchText, setSearchText] = useState('');

  const filteredUserList = useMemo(() => (filter === 'group' ? [] : userList), [filter, userList]);
  const filteredSelectedUserList = useMemo(() => (filter === 'group' ? [] : selectedUser), [filter, selectedUser]);
  const filteredGroupList = useMemo(() => (filter === 'user' ? [] : groupList), [filter, groupList]);
  const filteredSelectedGroupList = useMemo(() => (filter === 'user' ? [] : selectedGroup), [filter, selectedGroup]);

  const fullList = useMemo(() => sortByName([...userList, ...groupList]), [userList, groupList]);
  const usersNotSelected = useMemo(
    () => filteredUserList.filter((user) => !filteredSelectedUserList.some((selected) => user.id === selected.id)),
    [filteredUserList, filteredSelectedUserList],
  );
  const groupsNotSelected = useMemo(
    () => filteredGroupList.filter((group) => !filteredSelectedGroupList.some((selected) => group.id === selected.id)),
    [filteredGroupList, filteredSelectedGroupList],
  );
  const notSelected = useMemo(
    () => sortByName([...usersNotSelected, ...groupsNotSelected]),
    [usersNotSelected, groupsNotSelected],
  );
  const selected = useMemo(
    () => sortByName([...filteredSelectedUserList, ...filteredSelectedGroupList]),
    [filteredSelectedUserList, filteredSelectedGroupList],
  );
  const fullListResult = useMemo(() => sortByName([...usersResult, ...groupsResult]), [usersResult, groupsResult]);
  const searchResultsText = htmlT(t, 'groupAndUserSelection.searchResults', { count: fullListResult.length });
  const buttonText = t('groupAndUserSelection.submit', { count: selected.length });

  useEffect(() => {
    if (selectedUsers) setSelectedUser(selectedUsers);
  }, [selectedUsers]);

  useEffect(() => {
    if (selectedGroups) setSelectedGroup(selectedGroups);
  }, [selectedGroups]);

  useEffect(() => {
    if (searchText) {
      setUsersResult(
        usersNotSelected.filter((item: GroupedListModel) =>
          `${item.name} ${item.lastName}`?.toLowerCase().includes(searchText.toLowerCase()),
        ),
      );
      setGroupsResult(
        groupsNotSelected.filter((item: GroupedListModel) =>
          item.name?.toLowerCase().includes(searchText.toLowerCase()),
        ),
      );
      setShowSearch(true);
    } else {
      setUsersResult([]);
      setGroupsResult([]);
      setShowSearch(false);
    }
  }, [searchText, usersNotSelected, groupsNotSelected]);

  const handleClickFilter = (clicked: FilterState) => {
    if (clicked === filter) {
      setFilter(null);
    } else {
      setFilter(clicked);
    }
  };

  const handleSelection = (item: GroupedListModel) => {
    if (item.entity === 'user') return handleUserSelection(item);
    if (item.entity === 'group') return handleGroupSelection(item);
  };

  const handleUserSelection = (user: GroupedListModel) => {
    const isSelected = selectedUser.some((item) => item.id === user.id);

    if (isSelected) {
      setSelectedUser(selectedUser.filter((item) => item.id !== user.id));
    } else {
      setSelectedUser([...selectedUser, user]);
    }
  };

  const handleGroupSelection = (group: GroupedListModel) => {
    const isSelected = selectedGroup.some((item) => item.id === group.id);

    if (isSelected) {
      setSelectedGroup(selectedGroup.filter((item) => item.id !== group.id));
    } else {
      setSelectedGroup([...selectedGroup, group]);
    }
  };

  const handleSubmitSelection = () => {
    if (selected) onConfirmSelection(selected);
    onCloseSelectionScreen();
  };

  const handleOnSearch = (value: string) => setSearchText(value);

  return (
    <>
      <Mobile>
        <AppBar goBack={onCloseSelectionScreen}>
          <h5>{t('groupAndUserSelection.title')}</h5>
        </AppBar>
      </Mobile>

      {isLoading ? (
        <Loader fullScreen={isMobile} />
      ) : (
        <Container>
          <Desktop>
            <ModalHeader title={t('groupAndUserSelection.title')} onClose={onCloseSelectionScreen} />
          </Desktop>
          {fullList.length >= 10 && (
            <>
              <Search onSearch={handleOnSearch} fullWidth />
              <FiltersContainer>
                <FilterButton $active={filter === 'user'} onClick={() => handleClickFilter('user')}>
                  {t('groupAndUserSelection.filter.user')} <UserIcon />
                </FilterButton>
                <FilterButton $active={filter === 'group'} onClick={() => handleClickFilter('group')}>
                  {t('groupAndUserSelection.filter.group')} <GroupIcon />
                </FilterButton>
              </FiltersContainer>
            </>
          )}
          {showSearch ? (
            <List>
              <GroupedItem
                isSearch
                quantityText={searchResultsText}
                items={fullListResult}
                selectedItems={selected}
                selectableType="check"
                onSelection={handleSelection}
              />
            </List>
          ) : (
            <GroupedList
              isAlphabetic
              isSelectable
              items={notSelected}
              currentItem={selected}
              selectableType="check"
              onSelection={handleSelection}
              selectedQuantityKey="groupAndUserSelection.selected"
            />
          )}
          {selected.length > 0 && (
            <ButtonContainer onClick={handleSubmitSelection}>
              <StyledButton value={buttonText} />
            </ButtonContainer>
          )}
        </Container>
      )}
    </>
  );
};

export default GroupsAndUsersSelection;
